import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
  ColDef,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  LoadSuccessParams,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-enterprise";
import { AgBarSeriesOptions, AgChartOptions } from "ag-charts-enterprise";
import useSidebar from "src/hooks/useSidebar";
import useReference from "src/hooks/useReference";
import useTheme from "src/hooks/useTheme";
import { AgCharts } from "ag-charts-react";
import BaseGrid from "../../BaseGrid";
import ValidationService from "src/services/ValidationService";
import { CardAmountByDate } from "src/models/CardAmountByDate";
import { MerchantTransactionSummaryModel } from "src/models/MerchantTransactionSummaryModel";
import { MerchantSettlementTransactionDetailModel } from "src/models/MerchantSettlementTransactionDetailModel";
import MerchantTransactionDetailSummaryData from "./MerchantTransactionDetailSummaryData";
import { downloadFile } from "src/utils/DownloadFile";
import useReport from "src/hooks/useReport";
import SearchCriteria from "src/components/SearchCriteria";
import LoadingComponent from "src/components/LoadingComponent";
import { useParams } from "react-router-dom";

type ChartData = {
  amount: number;
  date: string;
};

const MerchantTransactionDetail = () => {
  const { category } = useParams();
  const { setIsOpen } = useSidebar();
  const { appSettings } = useReference();
  const { theme } = useTheme();
  const {
    reportInputs,
    setExceedsMaxDateRange,
    mapReportRequest,
    currencyFormatter,
    dateFormatter,
    timeFormatter,
    defaultColDef,
    getContextMenuItems,
    cardNumberFormatter
  } = useReport();
  const [summaryDetail, setSummaryDetail] =
    useState<MerchantTransactionSummaryModel>(
      {} as MerchantTransactionSummaryModel
    );
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [gridReady, setGridReady] = useState(false);
  const [dataSource, setDataSource] = useState<IServerSideDatasource>();
  const [gridApi, setGridApi] = useState<GridApi>();
  const [isDownload, setIsDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const nextOptions = { ...options };
    nextOptions.theme = theme === "dark" ? "ag-material-dark" : "ag-material";
    setOptions(nextOptions);
  }, [theme]);

  const handleViewReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_View
      )
    ) {
      setIsDownload(false);
      setExceedsMaxDateRange(false);
      setGridReady(true);
      setSummaryDetail({} as MerchantTransactionSummaryModel);
      setChartData([]);
      setDataSource(createServerSideDatasource());
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const handleDownloadReport = () => {
    if (
      ValidationService.isValidDateRange(
        reportInputs?.fromDate,
        reportInputs?.toDate,
        appSettings.dateRangeMonthsLimit_Download
      )
    ) {
      setIsDownload(true);
      setIsLoading(true);
      setExceedsMaxDateRange(false);
      ReportApi.exportMerchantSettlementTransactionDetails(
        mapReportRequest(null)
      )
        .then((res) => {
          downloadFile(
            `${MerchantTransactionDetail.displayName}-${category}`,
            res.data
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setExceedsMaxDateRange(true);
    }
  };

  const [colDefs] = useState<
    ColDef<MerchantSettlementTransactionDetailModel>[]
  >([
    { field: "cardNumber", valueFormatter: cardNumberFormatter },
    { field: "externalId" },
    { field: "transactionType" },
    { field: "campaign", cellDataType: "text" },
    { field: "merchant" },
    { field: "merchantGroup" },
    {
      field: "amount",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      headerName: "Date",
      field: "transactionTime",
      valueFormatter: dateFormatter,
    },
    {
      headerName: "Time",
      field: "transactionTime",
      valueFormatter: timeFormatter,
    },

    {
      field: "totalBasketSize",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },
    {
      field: "liftAmount",
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: "right", paddingRight: "50px" },
    },

    { field: "clientTrackingId", cellDataType: "text" },
    { field: "cardholderUserId", cellDataType: "text" },
    {
      field: "knownMerchant",
      cellRenderer: "agCheckboxCellRenderer",
      cellClassRules: {
        "center-checkbox": (params) => typeof params.value === "boolean",
      },
    },
  ]);

  const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
    return {
      type: "fitCellContents",
    };
  }, []);

  const createServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        getReportData(params);
      },
    };
  };

  const getReportData = (params: IServerSideGetRowsParams | null) => {
    setIsOpen(false);
    ReportApi.getMerchantSettlementTransactionDetail(mapReportRequest(params))
      .then((res) => {
        const result = res.data;
        const successParams: LoadSuccessParams = {
          rowData: result.rows,
          rowCount: result.totalCount,
        };
        if ((result?.rows?.length ?? 0) > 0) {
          params?.api?.hideOverlay();
          params?.success(successParams);
        } else {
          params?.api?.showNoRowsOverlay();
          params?.success({ rowData: [], rowCount: 0 });
        }
        setSummaryDetail(result.summaryDetail);
        const cd = mapChartData(result.summaryDetail);
        setChartData(cd);
        setOptions((prevOptions) => ({
          ...prevOptions,
          data: cd,
        }));
      })
      .catch(() => {
        params?.fail();
      });
  };

  const mapChartData = (data: MerchantTransactionSummaryModel) => {
    const cardAmountsByDate: CardAmountByDate[] = [];
    data.cardAmounts.map((ca) => {
      const cardAmount: CardAmountByDate = {
        amount: ca.amount,
        date: new Date(ca.date).toLocaleDateString(),
      };
      cardAmountsByDate.push(cardAmount);
    });
    return cardAmountsByDate;
  };

  const [options, setOptions] = useState<AgChartOptions>({
    title: {
      text: "Card Amount Total By Date",
    },
    // Data: Data to be displayed in the chart
    data: chartData as ChartData[],
    // Series: Defines which chart type and data to use
    series: [
      {
        type: "bar",
        xKey: "date",
        yKey: "amount",
      } as AgBarSeriesOptions,
    ],
    theme: theme === "dark" ? "ag-material-dark" : "ag-material",
  });

  return (
    <>
      <div className="report-container">
        <div className="report-header">
          <h5 className="mb-0">
            Merchant Transaction Detail -{" "}
            {category === "authorizations"
              ? "Authorizations Only"
              : "Settlements Only"}
          </h5>
          {(reportInputs?.programId ?? 0) > 0 &&
            reportInputs?.fromDate &&
            reportInputs?.toDate && (
              <div className="report-header-date">{`${reportInputs?.fromDate.toLocaleDateString()} - ${reportInputs?.toDate.toLocaleDateString()}`}</div>
            )}
        </div>
        <div className="blocks">
          <SearchCriteria
            usesLocations={false}
            usesUsers={false}
            usesCampaigns={true}
            handleViewReport={handleViewReport}
            handleDownloadReport={handleDownloadReport}
          />
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-summary">
              <MerchantTransactionDetailSummaryData
                summaryDetail={summaryDetail}
              />
            </div>
          )}
          {(chartData?.length ?? 0) > 0 && !isDownload && (
            <div className="report-chart">
              <AgCharts options={options} />
            </div>
          )}
        </div>
        {isDownload && isLoading ? (
          <LoadingComponent />
        ) : (
          !isDownload && (
            <BaseGrid
              colDefs={colDefs}
              defaultColDef={defaultColDef}
              getData={getReportData}
              isGridReady={gridReady}
              reportInputs={reportInputs}
              dataSource={dataSource}
              gridApi={gridApi}
              setGridApi={setGridApi}
              getContextMenuItems={getContextMenuItems}
              autoSizeStrategy={autoSizeStrategy}
            />
          )
        )}
      </div>
    </>
  );
};

MerchantTransactionDetail.displayName = "MerchantTransactionDetail";

export default MerchantTransactionDetail;
